import type { ComponentProps, ReactElement } from "react";
import { useEffect, useRef, useState } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

import useLatestValue from "app/custom-hooks/useLatestValue";

import FloatOverlayHook from "../FloatOverlayHook/FloatOverlayHook";
import type OverlayHook from "../OverlayHook/OverlayHook";

type Props = ComponentProps<typeof OverlayHook> & {
    getLastRow: (wrapper: HTMLDivElement) => Element | undefined;
    children: ReactElement;
};

const WrapperWithOverlayHook = ({ getLastRow, children, ...props }: Props): ReactElement => {
    const contentRef = useRef<HTMLDivElement>(null);
    const overlayWrapperRef = useRef<HTMLDivElement>(null);

    const [bottomPosition, setBottomPosition] = useState(0);

    const calculateBottomPositionRef = useLatestValue(() => {
        const target = contentRef.current;
        const overlayWrapper = overlayWrapperRef.current;

        if (!target || !overlayWrapper) {
            return;
        }

        const lastRow = getLastRow(target);

        if (!lastRow) {
            return;
        }

        const lastRowRect = lastRow.getBoundingClientRect();
        const overlayWrapperRect = overlayWrapper.getBoundingClientRect();

        const gap = overlayWrapperRect.bottom - lastRowRect.bottom;

        setBottomPosition(gap);
    });

    useEffect(() => {
        calculateBottomPositionRef.current();

        const target = contentRef.current;

        if (!target) {
            return;
        }

        const observer = new MutationObserver(() => {
            calculateBottomPositionRef.current();
        });

        observer.observe(target, {
            childList: true,
            subtree: true,
        });

        return () => observer.disconnect();
    }, []);

    return (
        <>
            <div ref={contentRef}>{children}</div>
            <Flex
                ref={overlayWrapperRef}
                position="relative"
                width="100%"
                height={0}
            >
                <FloatOverlayHook
                    bottom={bottomPosition}
                    {...props}
                />
            </Flex>
        </>
    );
};

export default WrapperWithOverlayHook;
